import {makeAutoObservable, runInAction} from 'mobx';
import {User} from '../../entity/model/user';
import {Auth} from 'aws-amplify';
import UserAgent from '../agent/user-agent';
import {RequestBody} from '../../entity/core/request-body';
import {UserOnboardResponseDto} from '../../entity/dto/user-onboard-response-dto';
import {UserInvitationDto} from '../../entity/dto/user-invitation-dto';

export default class UserStore {
  users: User[] = [];
  userInvitations: UserInvitationDto[] = [];
  currentUser: User | undefined = undefined;
  isLoggedIn = false;
  cache = false;
  userAgent: UserAgent;

  constructor(userAgent: UserAgent) {
    makeAutoObservable(this);
    this.userAgent = userAgent;
  }

  getCurrentUser = async (): Promise<User | undefined> => {
    try {
      this.currentUser = await this.userAgent.getCurrentUser();
      this.isLoggedIn = true;
    } catch (error: any) {
      console.error('Error occurred while fetch current user', error);
    }

    return this.currentUser;
  }

  getUserById = async (userId: string): Promise<User | undefined> => {
    try {
      return await this.userAgent.getUserById(userId);
    } catch(error:any) {
      console.error('Error occurred while fetch user', error);
    }
  }

  getAllUsers = async (organizationId: string): Promise<User[]> => {
    try {
      this.users = await this.userAgent.getAllUsers(organizationId);
      this.cache = true;
    } catch (error: any) {
      console.error('Error occurred while loading the users', error);
    }

    return this.users;
  }

  createUser = async (organizationID: string, requestBody: RequestBody): Promise<string | undefined> => {
    try {
      const response = await this.userAgent.createUser(organizationID, requestBody);
      if (response)
        this.cache = false;
    } catch (error: any) {
      console.error('Error occurred while creating the user', error);
      return 'Error occurred while creating the user !!';
    }
  }

  updateUser = async (organizationId: string, userId: string, requestBody: RequestBody): Promise<string | undefined> => {
    try {
      const response = await this.userAgent.updateUser(organizationId, userId, requestBody);
      if (response)
        this.cache = false;
    } catch (error: any) {
      console.error('Error occurred while updating the user', error);
      return 'Error occurred while updating the user !!';
    }
  }

  deleteUser = async (organizationId: string, userId: string): Promise<string | undefined> => {
    try {
      await this.userAgent.deleteUser(organizationId, userId);
      this.cache = false;
    } catch (error: any) {
      console.error('Error occurred while deleting the user', error);
      return 'Error occurred while deleting the user !!';
    }
  }

  detachUser = async (organizationId: string, userId: string): Promise<string | undefined> => {
    try {
      await this.userAgent.detachUser(organizationId, userId);
      this.cache = false;
    } catch (error: any) {
      console.error('Error occurred while detaching the user', error);
      return 'Error occurred while detaching the user !!';
    }
  }

  logout = async (): Promise<string | undefined> => {
    try {
      await Auth.signOut();

      runInAction(() => {
        this.isLoggedIn = false;
        this.currentUser = undefined;
      });
    } catch (error: any) {
      console.error('Error occurred while logout the user', error);
      return 'Error occurred while logout the user !!';
    }
  }

  onboardUser = async (userId: string, requestBody: RequestBody): Promise<string | undefined> => {
    try {
      const response = await this.userAgent.onboardUser(userId, requestBody);
      if (response.success)
        this.currentUser = response.user;
      else
        return response.message || 'Onboarding failed';
    } catch (error: any) {
      console.error('Error occurred while onboarding the user', error);
      return 'Error occurred while onboarding the user !!';
    }
  }

  sendInvitation = async (organizationID: string, requestBody: RequestBody): Promise<string> => {
    try {
      const response = await this.userAgent.sendInvitation(organizationID, requestBody);
      if (response)
        this.cache = false;
      return '';
    } catch (error: any) {
      console.error('Error occurred while sending user invitation', error);
      return 'Error occurred while sending user invitation !!';
    }
  }

  getUserInvitations = async (organizationId: string): Promise<UserInvitationDto[]> => {
    try {
      this.userInvitations = await this.userAgent.getUserInvitations(organizationId);
      this.cache = true;
    } catch (error: any) {
      console.error('Error occurred while loading user invitations', error);
    }

    return this.userInvitations;
  }

  resendInvitation = async (organizationId: string, invitationId: string): Promise<string> => {
    try {
      const response = await this.userAgent.resendInvitation(organizationId, invitationId);
      if (response) {
        const index = this.userInvitations.findIndex(inv => inv.id === invitationId);
        if (index !== -1)
          this.userInvitations[index] = response;
      }
      return '';
    } catch (error: any) {
      console.error('Error occurred while resending user invitation', error);
      return 'Error occurred while resending user invitation !!';
    }
  }

  deleteInvitation = async (organizationId: string, invitationId: string): Promise<string> => {
    try {
      await this.userAgent.deleteInvitation(organizationId, invitationId);
      this.userInvitations = this.userInvitations.filter(inv => inv.id !== invitationId);
      return '';
    } catch (error: any) {
      console.error('Error occurred while deleting user invitation', error);
      return 'Error occurred while deleting user invitation !!';
    }
  }

  acceptInvitation = async (invitationCode: string, firstName: string, lastName: string): Promise<string> => {
    try {
      await this.userAgent.acceptInvitation(invitationCode, firstName, lastName);
      return '';
    } catch (error: any) {
      console.error('Error occurred while accepting invitation', error);
      return 'Error occurred while accepting invitation. Please check your invitation code and try again.';
    }
  }
}
